import { useMe } from '@/hooks/use-auth';
import type { Role } from '@disparos/contracts';

/**
 * Hierarquia descendente: owner > admin > operator > viewer.
 * Espelha `apps/api/src/lib/auth/rbac.ts` — manter sincronizado.
 */
const ROLE_RANK: Record<Role, number> = {
  owner: 4,
  admin: 3,
  operator: 2,
  viewer: 1,
};

/**
 * Retorna `true` se o role atual do usuário >= `minRole`. Usado pra esconder
 * botões/ações que o backend bloquearia com 403. Single source of truth de
 * role aqui é `me.data.currentTenant.role` (reflete tenant ativo do switcher).
 *
 * Loading state retorna `false` (conservador — UI esconde até confirmar).
 *
 * Exemplos:
 * - `useCanRole('operator')` — pode criar/editar entidades
 * - `useCanRole('admin')` — pode deletar, gerenciar membros, revelar secrets
 * - `useCanRole('owner')` — reservado pra ações destrutivas no tenant
 */
export function useCanRole(minRole: Role): boolean {
  const me = useMe();
  const role = me.data?.currentTenant?.role;
  if (!role) return false;
  return ROLE_RANK[role] >= ROLE_RANK[minRole];
}

/** Retorna o role atual ou null se não autenticado / loading. */
export function useCurrentRole(): Role | null {
  const me = useMe();
  return me.data?.currentTenant?.role ?? null;
}
